import { NotFound, ProtectedRoute, TenantGate } from '@/auth';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet, RouteObject, useLocation } from 'react-router-dom';
import { SessionReplayController } from '@/posthog-analytics/SessionReplayController';
import { ProfileNameGate } from '@/auth/profile/ProfileNameGate';
import { SupportChat } from '@/mitra-agent/SupportChat';
import {
  LEGACY_SHARE_PROFILE_PATH,
  PUBLIC_INTERVIEW_PROFILE_PATH,
} from '@/candidate-profile/routes';
import { AppErrorBoundary } from '@/common/components/AppErrorBoundary';

const PublicAssessment = lazy(() => import('../candidate-profile/PublicAssessment'));
const PublicCandidate = lazy(
  () => import('../candidate-profile/PublicCandidate'),
);
const LegacyShareLinkRedirect = lazy(
  () => import('../candidate-profile/LegacyShareLinkRedirect'),
);
const AtsApp = lazy(() => import('../new-ats/AtsApp'));
const MeetingViewV3 = lazy(() => import('../meeting-view-v3/App'));
const JobListingPage = lazy(() => import('../job-portal/JobListingPage'));
const InterviewLobbyPage = lazy(
  () => import('../job-portal/InterviewLobbyPage'),
);
const ApplyPage = lazy(() => import('../job-portal/ApplyPage'));
const DesktopAuthPage = lazy(() => import('../job-portal/DesktopAuthPage'));
const BookingPage = lazy(() => import('../job-portal/BookingPage'));
const UnsubscribePage = lazy(() => import('../job-portal/UnsubscribePage'));
const WithdrawPage = lazy(() => import('../job-portal/WithdrawPage'));
const CodingAssessmentPage = lazy(
  () => import('../coding-assessment/CodingAssessmentPage'),
);

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
  </div>
);

const SuspenseLayout = () => {
  const { pathname } = useLocation();
  // The coding-assessment page has its own in-header Support launcher —
  // the global Mitra pill would be a redundant, mismatched-looking duplicate there.
  const showGlobalSupportChat = !pathname.startsWith('/coding-assessment');

  return (
    <Suspense fallback={<LoadingFallback />}>
      <TenantGate>
        <SessionReplayController />
        <ProfileNameGate />
        <Outlet />
        {showGlobalSupportChat && <SupportChat />}
      </TenantGate>
    </Suspense>
  );
};

const appRoutes: RouteObject[] = [
  {
    element: <SuspenseLayout />,
    children: [
      {
        path: '/',
        element: <JobListingPage />,
      },
      {
        path: '/job/:jobId',
        element: (
          <ProtectedRoute loginContext="job">
            <InterviewLobbyPage />
          </ProtectedRoute>
        ),
      },
      {
        // The pre-screen pilot's own surface, kept off `/job/:jobId` so the
        // lobby's state machine stays untouched while we learn whether the
        // pre-screen call is worth keeping. Unauthenticated because this is the
        // link clients post on social media: the role has to be readable before
        // anyone signs in, and the page gates itself at the Apply click.
        // `:jobSlug` is `216` or `216-applied-genai-engineer` — see lib/jobUrl.
        path: '/public/job/:jobSlug',
        element: <ApplyPage />,
      },
      {
        path: '/desktop-auth',
        element: (
          <ProtectedRoute loginContext="default">
            <DesktopAuthPage />
          </ProtectedRoute>
        ),
      },
      {
        path: PUBLIC_INTERVIEW_PROFILE_PATH,
        element: <PublicCandidate />,
      },
      {
        // The assessment counterpart. `:assessmentId` is carried for readability
        // only: the token identifies the assessment, as it does on the profile link.
        path: '/public/assessment/:assessmentId/:token',
        element: <PublicAssessment />,
      },
      {
        // Retired 30 days after the cutover reaches prod, once every legacy token has expired.
        path: LEGACY_SHARE_PROFILE_PATH,
        element: <LegacyShareLinkRedirect />,
      },
      {
        // The email "Unsubscribe" link. Token-less (just the interview id, like the booking
        // link) — requires candidate login + interview ownership (same gate as booking), so a
        // leaked/forwarded link alone can't opt someone out. Declared before
        // /booking/:interviewId so the static "unsubscribe" segment wins the match.
        path: '/booking/unsubscribe/:interviewId',
        element: (
          <ProtectedRoute loginContext="booking">
            <UnsubscribePage />
          </ProtectedRoute>
        ),
      },
      {
        // The email "Withdraw your application" link. Same token-less gate as unsubscribe, and
        // likewise declared before /booking/:interviewId so the static segment wins the match.
        path: '/booking/withdraw/:interviewId',
        element: (
          <ProtectedRoute loginContext="booking">
            <WithdrawPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/booking/:interviewId',
        element: (
          <ProtectedRoute loginContext="booking">
            <BookingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/ats/*',
        element: (
          <ProtectedRoute loginContext="dashboard">
            <AppErrorBoundary boundary="ats">
              <AtsApp />
            </AppErrorBoundary>
          </ProtectedRoute>
        ),
      },
      {
        path: '/coding-assessment/:assessmentId',
        element: (
          <ProtectedRoute loginContext="assessment">
            <AppErrorBoundary boundary="coding-assessment">
              <CodingAssessmentPage />
            </AppErrorBoundary>
          </ProtectedRoute>
        ),
      },
      {
        path: '/interview/:interview_id/assessment',
        element: (
          <ProtectedRoute loginContext="default">
            <AppErrorBoundary boundary="meeting-view">
              <MeetingViewV3 />
            </AppErrorBoundary>
          </ProtectedRoute>
        ),
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
];

export const router = createBrowserRouter(appRoutes);
